import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import store from './store'
import { tokenManager } from './services/api'

// Layouts
import AdminLayout from './components/layout/AdminLayout.vue'
import MemberDetailLayout from './components/layout/MemberDetailLayout.vue'

// Auth
import LoginPage from './pages/auth/LoginPage.vue'

// Admin (유저/관리자용)
import MemberList from './pages/admin/MemberList.vue'
import UserList from './pages/admin/UserList.vue'
import CrawlPage from './pages/admin/CrawlPage.vue'
import CertManagePage from './pages/admin/CertManagePage.vue'
import ConsultRequestManagePage from './pages/admin/ConsultRequestManagePage.vue'
import HolidayManagePage from './pages/admin/HolidayManagePage.vue'
import PromoMailListPage from './pages/admin/PromoMailListPage.vue'
import LtcsScheduleProbePage from './pages/admin/LtcsScheduleProbePage.vue'
import HeumieumUploadRequestPage from './pages/admin/HeumieumUploadRequestPage.vue'
import LtcsScheduleScreenPage from './pages/admin/LtcsScheduleScreenPage.vue'
import JobTypeManagePage from './pages/admin/JobTypeManagePage.vue'
import SugaManagePage from './pages/admin/SugaManagePage.vue'
import TaxRateManagePage from './pages/admin/TaxRateManagePage.vue'
import AdminDashboardPage from './pages/admin/AdminDashboardPage.vue'
import BoardPage from './pages/admin/BoardPage.vue'

// Facility (시설검색)
import CrawlDataExplorerPage from './pages/facility/CrawlDataExplorerPage.vue'
import CrawlDetailPopup from './pages/facility/CrawlDetailPopup.vue'

// Member (기관/멤버용)
import MemberMyInfo from './pages/member/MemberMyInfo.vue'
import MemberDetailInfo from './pages/member/detail/MemberDetailInfo.vue'
import MemberDetailBankbookTest from './pages/member/detail/MemberDetailBankbookTest.vue'
import MemberDetailClosingHistory from './pages/member/detail/MemberDetailClosingHistory.vue'
import MemberDetailAccountingReports from './pages/member/detail/MemberDetailAccountingReports.vue'
import MemberBudgetRevision from './pages/member/detail/MemberBudgetRevision.vue'
import LtcsRequestPage from './pages/member/request/LtcsRequestPage.vue'
import CesRequestPage from './pages/member/request/CesRequestPage.vue'
import HomeBeneficiaryPage from './pages/member/beneficiary/HomeBeneficiaryPage.vue'
import MemberHomeWageMonthlySetting from './pages/member/wage/MemberHomeWageMonthlySetting.vue'
import MemberFacilityWageMonthlySetting from './pages/member/wage/MemberFacilityWageMonthlySetting.vue'
import MemberWageHrManagement from './pages/member/wage/MemberWageHrManagement.vue'

const routes: readonly RouteRecordRaw[] = [
  // 공통 라우트
  {
    path: '/login',
    component: LoginPage,
    name: 'login',
    meta: { title: '로그인', requiresAuth: false }
  },
  
  // 기본: 관리자(90+)는 대시보드, 그 외 유저는 고객관리
  {
    path: '/',
    redirect: () => {
      const hasToken = !!tokenManager.getToken();
      const loggedIn = hasToken && store.getters.isLoggedIn;
      if (!loggedIn) return '/login';
      return getHomeByUserType(store.getters.userType, Number(store.getters.roleLevel || 0));
    }
  },
  {
    path: '/dashboard',
    component: AdminDashboardPage,
    name: 'adminDashboard',
    meta: { title: '대시보드', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 60 }
  },
  {
    path: '/board',
    component: BoardPage,
    name: 'board',
    meta: { title: '게시판', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 60 }
  },
  { path: '/notice', redirect: '/board' },
  { path: '/inquiry', redirect: '/board' },
  {
    path: '/member',
    component: MemberList,
    name: 'member',
    meta: { title: '고객관리', requiresAuth: true, forAdmin: true, roleBasedLayout: true }
  },
  
  // 관리자/유저 공용 라우트 - 시설검색
  {
    path: '/facility',
    component: CrawlDataExplorerPage,
    name: 'facility',
    meta: { title: '시설검색', requiresAuth: true, forAdmin: true, roleBasedLayout: true }
  },
  
  // 구 유저 인사관리 경로 → 고객관리
  { path: '/hr', redirect: '/member' },

  // 관리자(ADMIN+) 전용 라우트 (role_level 90+)
  {
    path: '/user',
    component: UserList,
    name: 'user',
    meta: { title: '인사관리', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 80 }
  },
  {
    path: '/heumieum-upload',
    component: HeumieumUploadRequestPage,
    name: 'heumieumUpload',
    meta: { title: '희망이음 업로드', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 100 }
  },
  {
    path: '/crawl',
    component: CrawlPage,
    name: 'crawl',
    meta: { title: '크롤링 관리', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 100 }
  },
  {
    path: '/cert-manage',
    component: CertManagePage,
    name: 'certManage',
    meta: { title: '인증서 관리', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 100 }
  },
  {
    path: '/consult-requests',
    component: ConsultRequestManagePage,
    name: 'consultRequests',
    meta: { title: '상담요청관리', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 80 }
  },
  {
    path: '/briefing-applications',
    component: ConsultRequestManagePage,
    name: 'briefingApplications',
    props: { lockedKind: '급식통상임금설명회신청' },
    meta: { title: '설명회신청관리', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 60 }
  },
  {
    path: '/holiday-manage',
    component: HolidayManagePage,
    name: 'holidayManage',
    meta: { title: '공휴일관리', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 80 }
  },
  {
    path: '/promo-mail-list',
    component: PromoMailListPage,
    name: 'promoMailList',
    meta: { title: '홍보', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 80 }
  },
  {
    path: '/ltcs-schedule-probe',
    component: LtcsScheduleProbePage,
    name: 'ltcsScheduleProbe',
    meta: { title: '일정계획 변경 테스트', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 100 }
  },
  {
    path: '/job-type-manage',
    component: JobTypeManagePage,
    name: 'jobTypeManage',
    meta: { title: '직종관리', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 80 }
  },
  {
    path: '/suga-manage',
    component: SugaManagePage,
    name: 'sugaManage',
    meta: { title: '수가변경', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 80 }
  },
  {
    path: '/tax-rate-manage',
    component: TaxRateManagePage,
    name: 'taxRateManage',
    meta: { title: '요율·최저시급', requiresAuth: true, forAdmin: true, layout: AdminLayout, minRole: 80 }
  },
  {
    path: '/ltcs-schedule-screen',
    component: LtcsScheduleScreenPage,
    name: 'ltcsScheduleScreen',
    meta: { title: '크롤링 화면', requiresAuth: true, forAdmin: true, minRole: 100 }
  },
  
  // 멤버(기관)용 라우트 - MemberDetailLayout 사용 (멤버 로그인 모드)
  {
    path: '/m',
    component: MemberDetailLayout,
    meta: { requiresAuth: true, forMember: true },
    children: [
      {
        path: '',
        redirect: { name: 'memberInfo' }
      },
      {
        path: 'info',
        component: MemberDetailInfo,
        name: 'memberInfo',
        meta: { title: '시설정보', requiresAuth: true, forMember: true }
      },
      {
        path: 'employee',
        redirect: (to) => ({ name: 'memberWageHr', query: { ...to.query, tab: 'employee' } })
      },
      {
        path: 'beneficiary',
        redirect: (to) => ({ name: 'memberWageHr', query: { ...to.query, tab: 'beneficiary' } })
      },
      {
        path: 'wage/hr',
        component: MemberWageHrManagement,
        name: 'memberWageHr',
        meta: { title: '인사관리', requiresAuth: true, forMember: true }
      },
      {
        path: 'beneficiary/home',
        component: HomeBeneficiaryPage,
        name: 'memberBeneficiaryHome',
        meta: { title: '수급자 일정관리 (방문요양/목욕)', requiresAuth: true, forMember: true }
      },
      {
        path: 'budget',
        redirect: { name: 'memberBudgetRevision' },
      },
      {
        path: 'budget-revision',
        component: MemberBudgetRevision,
        name: 'memberBudgetRevision',
        meta: { title: '예산추경관리', requiresAuth: true, forMember: true }
      },
      {
        path: 'wage/monthly-setting/home',
        component: MemberHomeWageMonthlySetting,
        name: 'memberWageMonthlySettingHome',
        meta: { title: '임금설정 (방문요양)', requiresAuth: true, forMember: true }
      },
      {
        path: 'wage/monthly-setting/home-test',
        redirect: (to) => ({
          name: 'memberWageMonthlySettingHome',
          params: to.params,
          query: to.query,
        }),
      },
      {
        path: 'wage/monthly-setting/facility',
        component: MemberFacilityWageMonthlySetting,
        name: 'memberWageMonthlySettingFacility',
        meta: { title: '임금설정 (요양시설)', requiresAuth: true, forMember: true }
      },
      {
        path: 'bankbook',
        component: MemberDetailBankbookTest,
        name: 'memberBankbook',
        meta: { title: '월별통장내역', requiresAuth: true, forMember: true }
      },
      {
        // (구) 테스트 주소 → 정식 월별통장내역으로 흡수
        path: 'bankbook-test',
        name: 'memberBankbookTest',
        redirect: (to) => ({ name: 'memberBankbook', params: to.params, query: to.query }),
      },
      {
        path: 'closing-history',
        component: MemberDetailClosingHistory,
        name: 'memberClosingHistory',
        meta: { title: '마감요청내역', requiresAuth: true, forMember: true }
      },
      {
        path: 'accounting-reports',
        component: MemberDetailAccountingReports,
        name: 'memberAccountingReports',
        meta: { title: '회계보고서류', requiresAuth: true, forMember: true }
      },
      {
        path: 'request/ltcs',
        component: LtcsRequestPage,
        name: 'memberRequestLtcs',
        meta: { title: '롱텀케어 자료요청', requiresAuth: true, forMember: true, minRole: 90 }
      },
      {
        path: 'request/ces',
        component: CesRequestPage,
        name: 'memberRequestCes',
        meta: { title: '희망이음 자료요청', requiresAuth: true, forMember: true, minRole: 90 }
      },
      {
        path: 'myinfo',
        component: MemberMyInfo,
        name: 'memberMyInfo',
        meta: { title: '내 정보', requiresAuth: true, forMember: true }
      }
    ]
  },
  
  // 시설 상세 팝업 (새 창용, 레이아웃 없음)
  {
    path: '/facility/detail/:ccNumber',
    component: CrawlDetailPopup,
    name: 'facilityDetailPopup',
    meta: { title: '시설 상세정보', requiresAuth: true, isPopup: true }
  },
  
  // 시설 상세 팝업 - 고객관리용 (MemberDetailLayout 사용)
  {
    path: '/member/detail/:memberCode',
    component: MemberDetailLayout,
    meta: { requiresAuth: true, isPopup: true },
    children: [
      {
        path: '',
        component: MemberDetailInfo,
        name: 'memberDetailInfo',
        meta: { title: '시설정보', requiresAuth: true, isPopup: true }
      },
      {
        path: 'request/ltcs',
        component: LtcsRequestPage,
        name: 'memberDetailRequestLtcs',
        meta: { title: '롱텀케어 자료요청', requiresAuth: true, isPopup: true, minRole: 90 }
      },
      {
        path: 'request/ces',
        component: CesRequestPage,
        name: 'memberDetailRequestCes',
        meta: { title: '희망이음 자료요청', requiresAuth: true, isPopup: true, minRole: 90 }
      },
      {
        path: 'employee',
        redirect: (to) => ({
          name: 'memberDetailWageHr',
          params: to.params,
          query: { ...to.query, tab: 'employee' }
        })
      },
      {
        path: 'beneficiary',
        redirect: (to) => ({
          name: 'memberDetailWageHr',
          params: to.params,
          query: { ...to.query, tab: 'beneficiary' }
        })
      },
      {
        path: 'wage/hr',
        component: MemberWageHrManagement,
        name: 'memberDetailWageHr',
        meta: { title: '인사관리', requiresAuth: true, isPopup: true }
      },
      {
        path: 'beneficiary/home',
        component: HomeBeneficiaryPage,
        name: 'memberDetailBeneficiaryHome',
        meta: { title: '수급자 일정관리 (방문요양)', requiresAuth: true, isPopup: true }
      },
      {
        path: 'budget-revision',
        component: MemberBudgetRevision,
        name: 'memberDetailBudgetRevision',
        meta: { title: '예산추경관리', requiresAuth: true, isPopup: true }
      },
      {
        path: 'wage/monthly-setting/home',
        component: MemberHomeWageMonthlySetting,
        name: 'memberDetailWageMonthlySettingHome',
        meta: { title: '임금설정 (방문요양)', requiresAuth: true, isPopup: true }
      },
      {
        path: 'wage/monthly-setting/home-test',
        redirect: (to) => ({
          name: 'memberDetailWageMonthlySettingHome',
          params: to.params,
          query: to.query,
        }),
      },
      {
        path: 'wage/monthly-setting/facility',
        component: MemberFacilityWageMonthlySetting,
        name: 'memberDetailWageMonthlySettingFacility',
        meta: { title: '임금설정 (요양시설)', requiresAuth: true, isPopup: true }
      },
      {
        path: 'bankbook',
        component: MemberDetailBankbookTest,
        name: 'memberDetailBankbook',
        meta: { title: '월별통장내역', requiresAuth: true, isPopup: true }
      },
      {
        // (구) 테스트 주소 → 정식 월별통장내역으로 흡수
        path: 'bankbook-test',
        name: 'memberDetailBankbookTest',
        redirect: (to) => ({ name: 'memberDetailBankbook', params: to.params, query: to.query }),
      },
      {
        path: 'closing-history',
        component: MemberDetailClosingHistory,
        name: 'memberDetailClosingHistory',
        meta: { title: '마감요청내역', requiresAuth: true, isPopup: true }
      },
      {
        path: 'accounting-reports',
        component: MemberDetailAccountingReports,
        name: 'memberDetailAccountingReports',
        meta: { title: '회계보고서류', requiresAuth: true, isPopup: true }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 사용자 타입에 따른 홈 경로
const getHomeByUserType = (userType: string | null, roleLevel = 0): string => {
  if (userType === 'member') {
    return '/m/info';
  }
  if (roleLevel >= 60) {
    return '/dashboard';
  }
  return '/member';
};

// 네비게이션 가드
router.beforeEach((to, from, next) => {
  // 페이지 타이틀 설정
  document.title = `CAION - ${to.meta.title || '통합 관리 시스템'}`;
  
  // 세션 복원은 main.ts에서 앱 마운트 전에 이미 완료됨
  
  // 스토어 상태와 토큰 존재 여부 모두 체크 (불일치 방지)
  const hasToken = !!tokenManager.getToken();
  const hasUser = store.getters.isLoggedIn;
  
  // 토큰은 없는데 스토어에 유저가 있으면 스토어 클리어 (세션 만료 상태)
  if (!hasToken && hasUser) {
    console.warn('토큰 없음 - 스토어 인증 정보 클리어');
    store.commit('clearAuth');
  }
  
  const isLoggedIn = hasToken && hasUser;
  const userType = store.getters.userType;
  const roleLevel = Number(store.getters.roleLevel || 0);
  const requiresAuth = to.meta.requiresAuth !== false;
  
  // 로그인 페이지에서 이미 로그인된 경우
  if (to.path === '/login' && isLoggedIn) {
    next(getHomeByUserType(userType, roleLevel));
    return;
  }
  
  // 인증이 필요한 페이지인데 로그인 안 된 경우
  if (requiresAuth && !isLoggedIn) {
    next('/login');
    return;
  }
  
  // 멤버가 관리자 페이지 접근 시
  if (isLoggedIn && userType === 'member' && to.meta.forAdmin) {
    next('/m/info');
    return;
  }
  
  // 관리자(유저)가 멤버 페이지 접근 시 (대리 로그인이 아닌 경우)
  if (isLoggedIn && userType === 'user' && to.meta.forMember) {
    const isImpersonating = store.getters.isImpersonating;
    if (!isImpersonating) {
      next(getHomeByUserType(userType, roleLevel));
      return;
    }
  }

  // 최소 권한 레벨 체크 (SUPER_ADMIN 전용 등은 actualRoleLevel 우선)
  const minRole = Number(to.meta.minRole || 0);
  const effectiveRoleLevel = Number(
    store.getters.actualRoleLevel ?? store.getters.roleLevel ?? roleLevel ?? 0
  );
  if (isLoggedIn && minRole > 0 && effectiveRoleLevel < minRole) {
    next(getHomeByUserType(userType, roleLevel));
    return;
  }
  
  // 최대 권한 레벨 체크 (비관리자 홈 등)
  const maxRole = Number(to.meta.maxRole || 0);
  if (isLoggedIn && maxRole > 0 && roleLevel > maxRole) {
    next(getHomeByUserType(userType, roleLevel));
    return;
  }
  
  next();
});

export default router
